import Ajv from 'ajv'
import { evaluateSchemaProperty } from '@cibo/core'
import { TokenContext } from '../core/TokenContext'

export type JSONSchemaType = {
  type?: string | string[]
  properties?: Record<string, JSONSchemaType>
  items?: JSONSchemaType | JSONSchemaType[]
  required?: string[]
  if?: JSONSchemaType
  then?: JSONSchemaType
  else?: JSONSchemaType
  allOf?: JSONSchemaType[]
  anyOf?: JSONSchemaType[]
  oneOf?: JSONSchemaType[]
  $ref?: string
  $defs?: Record<string, JSONSchemaType>
  definitions?: Record<string, JSONSchemaType>
  enum?: unknown[]
  const?: unknown
  default?: unknown
  title?: string
  description?: string
  format?: string
  minimum?: number
  maximum?: number
  minLength?: number
  maxLength?: number
  minItems?: number
  maxItems?: number
  pattern?: string
  multipleOf?: number
  uniforms?: Record<string, unknown>
  additionalProperties?: boolean | JSONSchemaType
  [key: string]: unknown
}

const defaultAjv = new Ajv({ allErrors: true, strict: false, validateFormats: false })

/**
 * Merge two JSON Schema objects, combining properties, required, etc.
 */
function mergeSchemas(base: JSONSchemaType, overlay: JSONSchemaType): JSONSchemaType {
  const result = { ...base }

  // Merge properties
  if (overlay.properties) {
    result.properties = { ...result.properties }
    for (const [key, value] of Object.entries(overlay.properties)) {
      if (result.properties[key]) {
        result.properties[key] = mergeSchemas(result.properties[key], value)
      } else {
        result.properties[key] = value
      }
    }
  }

  // Merge required
  if (overlay.required) {
    const baseRequired = result.required ?? []
    result.required = [...new Set([...baseRequired, ...overlay.required])]
  }

  // Copy over scalar fields from overlay
  for (const key of Object.keys(overlay)) {
    if (key === 'properties' || key === 'required') continue
    if (key === 'if' || key === 'then' || key === 'else') continue
    if (key === 'allOf' || key === 'anyOf' || key === 'oneOf') continue
    ;(result as Record<string, unknown>)[key] = (overlay as Record<string, unknown>)[key]
  }

  return result
}

/**
 * Resolve $ref pointers within a schema.
 */
function resolveRef(schema: JSONSchemaType, rootSchema: JSONSchemaType): JSONSchemaType {
  if (!schema.$ref) return schema

  const refPath = schema.$ref.replace(/^#\//, '').split('/')
  let resolved: JSONSchemaType = rootSchema
  for (const segment of refPath) {
    resolved = (resolved as Record<string, JSONSchemaType>)[segment]
    if (!resolved) {
      throw new Error(`Cannot resolve $ref: ${schema.$ref}`)
    }
  }

  // Merge ref result with any other properties from the referring schema
  const { $ref: _, ...rest } = schema
  const resolvedRef = resolveRef(resolved, rootSchema)

  // Use mergeSchemas to properly combine properties, required, and other fields
  // instead of simple spread which would lose the ref's properties
  return mergeSchemas(resolvedRef, rest)
}

/**
 * Deep-resolve all $ref pointers in a schema tree.
 */
function resolveAllRefs(schema: JSONSchemaType, rootSchema: JSONSchemaType): JSONSchemaType {
  let resolved = resolveRef(schema, rootSchema)
  resolved = { ...resolved }

  if (resolved.properties) {
    resolved.properties = { ...resolved.properties }
    for (const [key, value] of Object.entries(resolved.properties)) {
      resolved.properties[key] = resolveAllRefs(value, rootSchema)
    }
  }

  if (resolved.items) {
    if (Array.isArray(resolved.items)) {
      resolved.items = resolved.items.map(item => resolveAllRefs(item, rootSchema))
    } else {
      resolved.items = resolveAllRefs(resolved.items, rootSchema)
    }
  }

  for (const keyword of ['allOf', 'anyOf', 'oneOf'] as const) {
    if (resolved[keyword]) {
      resolved[keyword] = (resolved[keyword] as JSONSchemaType[]).map(s =>
        resolveAllRefs(s, rootSchema)
      )
    }
  }

  if (resolved.if) resolved.if = resolveAllRefs(resolved.if, rootSchema)
  if (resolved.then) resolved.then = resolveAllRefs(resolved.then, rootSchema)
  if (resolved.else) resolved.else = resolveAllRefs(resolved.else, rootSchema)

  return resolved
}

/**
 * Evaluate whether data matches an if-schema using ajv.
 */
function evaluateCondition(condition: JSONSchemaType, data: unknown, ajv: Ajv): boolean {
  try {
    const validate = ajv.compile(condition)
    return validate(data) as boolean
  } catch {
    return false
  }
}

/**
 * Core algorithm: Resolve a JSON Schema with conditionals against actual data
 * to produce the "effective" schema — only containing properties from
 * matching conditional branches.
 *
 * @param schema  The JSON Schema (possibly with if/then/else, allOf, oneOf, anyOf)
 * @param data    The current model data to evaluate conditionals against
 * @param rootSchema The root schema for $ref resolution
 * @param ajv     AJV validator instance
 * @param tokenContext Optional token context for evaluating template expressions in schema properties
 * @returns The effective (resolved) JSON Schema with no conditionals
 */
// eslint-disable-next-line visual/complexity
export function resolveEffectiveSchema(
  schema: JSONSchemaType,
  data: unknown = {},
  rootSchema?: JSONSchemaType,
  ajv: Ajv = defaultAjv,
  tokenContext?: TokenContext
): JSONSchemaType {
  const root = rootSchema ?? schema

  // Step 1: Resolve all $refs
  let resolved = resolveAllRefs(schema, root)

  // Step 2: Start with base schema (strip conditional keywords)
  let effective: JSONSchemaType = {}
  for (const [key, value] of Object.entries(resolved)) {
    if (
      key !== 'if' &&
      key !== 'then' &&
      key !== 'else' &&
      key !== 'allOf' &&
      key !== 'anyOf' &&
      key !== 'oneOf'
    ) {
      ;(effective as Record<string, unknown>)[key] = value
    }
  }

  // Detect whether oneOf/anyOf represent value-level options (e.g. enum-like
  // const/label lists on a string field) vs schema-level conditionals.
  // Value-level options are preserved as-is; only schema-level branches get
  // evaluated against model data.
  const isValueOptions = (branches: JSONSchemaType[] | undefined): boolean => {
    if (!branches || branches.length === 0) return false
    return branches.every(b => b.const !== undefined || b.enum !== undefined)
  }

  const oneOfIsOptions = isValueOptions(resolved.oneOf)
  const anyOfIsOptions = isValueOptions(resolved.anyOf)

  // Keep value-level oneOf/anyOf on the effective schema so field components
  // can read them for rendering select/radio options.
  if (oneOfIsOptions) {
    effective.oneOf = resolved.oneOf
  }
  if (anyOfIsOptions) {
    effective.anyOf = resolved.anyOf
  }

  // Step 3: Handle if/then/else
  if (resolved.if) {
    const matches = evaluateCondition(resolved.if, data, ajv)
    if (matches && resolved.then) {
      const thenResolved = resolveEffectiveSchema(resolved.then, data, root, ajv, tokenContext)
      effective = mergeSchemas(effective, thenResolved)
    } else if (!matches && resolved.else) {
      const elseResolved = resolveEffectiveSchema(resolved.else, data, root, ajv, tokenContext)
      effective = mergeSchemas(effective, elseResolved)
    }
  }

  // Step 4: Handle allOf — all branches are merged (each may have its own conditionals)
  if (resolved.allOf) {
    for (const subSchema of resolved.allOf) {
      const subResolved = resolveEffectiveSchema(subSchema, data, root, ajv, tokenContext)
      effective = mergeSchemas(effective, subResolved)
    }
  }

  // Step 5: Handle oneOf — find the first matching branch
  // Skip when oneOf represents value options (const/label pairs).
  if (resolved.oneOf && !oneOfIsOptions) {
    for (const subSchema of resolved.oneOf) {
      if (evaluateCondition(subSchema, data, ajv)) {
        const subResolved = resolveEffectiveSchema(subSchema, data, root, ajv, tokenContext)
        effective = mergeSchemas(effective, subResolved)
        break
      }
    }
  }

  // Step 6: Handle anyOf — merge all matching branches
  // Skip when anyOf represents value options (const/label pairs).
  if (resolved.anyOf && !anyOfIsOptions) {
    for (const subSchema of resolved.anyOf) {
      if (evaluateCondition(subSchema, data, ajv)) {
        const subResolved = resolveEffectiveSchema(subSchema, data, root, ajv, tokenContext)
        effective = mergeSchemas(effective, subResolved)
      }
    }
  }

  // Step 7: Recursively resolve properties
  if (effective.properties) {
    effective.properties = { ...effective.properties }
    for (const [key, propSchema] of Object.entries(effective.properties)) {
      const propData =
        data && typeof data === 'object' ? (data as Record<string, unknown>)[key] : undefined
      effective.properties[key] = resolveEffectiveSchema(propSchema, propData, root, ajv, tokenContext)
    }
  }

  // Step 8: Do NOT resolve array items here.
  // Array items contain conditionals that must be evaluated per-item
  // against each item's actual data. Resolution happens in:
  //   - ListField (for rendering)
  //   - stripModel (for model cleanup)

  // Step 9: Evaluate template expressions in schema properties
  if (tokenContext) {
    effective = evaluateAllSchemaProperties(effective, tokenContext)
  }

  return effective
}

/**
 * Resolve effective schema for a specific array item.
 * The items schema is evaluated against the individual item's data.
 */
export function resolveEffectiveSchemaForArrayItem(
  itemsSchema: JSONSchemaType,
  itemData: unknown,
  rootSchema: JSONSchemaType,
  ajv: Ajv = defaultAjv,
  tokenContext?: TokenContext
): JSONSchemaType {
  return resolveEffectiveSchema(itemsSchema, itemData, rootSchema, ajv, tokenContext)
}

/**
 * Recursively evaluate template expressions in all schema properties.
 * This allows dynamic schema values like formatMinimum: "${year - 1}-04-16"
 */
function evaluateAllSchemaProperties(
  schema: JSONSchemaType,
  tokenContext: TokenContext
): JSONSchemaType {
  const result = { ...schema }

  // Evaluate all direct properties (not the meta-properties like 'properties', 'items', etc.)
  for (const [key, value] of Object.entries(result)) {
    // Skip structural properties that shouldn't be evaluated
    if (
      key === 'properties' ||
      key === 'items' ||
      key === 'allOf' ||
      key === 'anyOf' ||
      key === 'oneOf' ||
      key === 'if' ||
      key === 'then' ||
      key === 'else' ||
      key === '$defs' ||
      key === 'definitions' ||
      key === 'additionalProperties'
    ) {
      continue
    }

    // Evaluate this property
    ;(result as Record<string, unknown>)[key] = evaluateSchemaProperty(value, tokenContext)
  }

  // Recursively evaluate nested properties
  if (result.properties) {
    result.properties = { ...result.properties }
    for (const [key, propSchema] of Object.entries(result.properties)) {
      result.properties[key] = evaluateAllSchemaProperties(propSchema, tokenContext)
    }
  }

  // Recursively evaluate array items schema
  if (result.items) {
    if (Array.isArray(result.items)) {
      result.items = result.items.map(item => evaluateAllSchemaProperties(item, tokenContext))
    } else {
      result.items = evaluateAllSchemaProperties(result.items, tokenContext)
    }
  }

  return result
}

/**
 * Get the list of property names from an effective schema.
 */
export function getEffectivePropertyNames(schema: JSONSchemaType): string[] {
  return schema.properties ? Object.keys(schema.properties) : []
}
